import { useState } from "react";
import { updatePassword } from "../../services/auth/auth";

const FIELDS = [
  { name: "oldPassword", label: "Current password", hint: "Enter the password you use today." },
  { name: "newPassword", label: "New password", hint: "Use at least 6 characters." },
  { name: "confirmPassword", label: "Confirm new password", hint: "Re-enter the new password." },
];

const PasswordCard = ({ onSaved }) => {
  const [passwords, setPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [visible, setVisible] = useState({});
  const [saving, setSaving] = useState(false);

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validatePasswords = () => {
    const nextErrors = {};
    if (!passwords.oldPassword) nextErrors.oldPassword = "Current password is required.";
    if (!passwords.newPassword) nextErrors.newPassword = "New password is required.";
    else if (passwords.newPassword.length < 6) nextErrors.newPassword = "Password must be at least 6 characters.";
    if (!passwords.confirmPassword) nextErrors.confirmPassword = "Confirm your new password.";
    else if (passwords.newPassword !== passwords.confirmPassword) nextErrors.confirmPassword = "Passwords do not match.";
    return nextErrors;
  };

  const handleChangePassword = async () => {
    const validationErrors = validatePasswords();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (saving) return;

    setSaving(true);
    try {
      const message = await updatePassword({
        currentPassword: passwords.oldPassword,
        newPassword: passwords.newPassword,
        confirmPassword: passwords.confirmPassword,
      });
      setPasswords({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setVisible({});
      setErrors({});
      onSaved?.({
        title: "Password updated",
        message: typeof message === "string" && message.trim()
          ? message
          : "Your password was changed successfully. Use it the next time you sign in.",
      });
    } catch {
      // The request interceptor already shows the server error.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-form">
      <div className="settings-security-note">
        <i className="bi bi-shield-check" />
        <p>Choose a password you do not use anywhere else. This change applies only to your portal login.</p>
      </div>

      <div className="settings-grid settings-grid--single">
        {FIELDS.map((field) => (
          <div className="settings-field" key={field.name}>
            <label className="settings-field__label" htmlFor={field.name}>{field.label}</label>
            <div className="settings-input-wrap">
              <input
                id={field.name}
                type={visible[field.name] ? "text" : "password"}
                name={field.name}
                value={passwords[field.name]}
                onChange={handlePasswordChange}
                className={`settings-input${errors[field.name] ? " is-invalid" : ""}`}
                autoComplete={field.name === "oldPassword" ? "current-password" : "new-password"}
              />
              <button
                type="button"
                className="settings-eye"
                onClick={() => setVisible((prev) => ({ ...prev, [field.name]: !prev[field.name] }))}
                aria-label={visible[field.name] ? "Hide password" : "Show password"}
              >
                <i className={`bi ${visible[field.name] ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>
            {errors[field.name] ? (
              <span className="settings-error">{errors[field.name]}</span>
            ) : (
              <span className="settings-hint">{field.hint}</span>
            )}
          </div>
        ))}
      </div>

      <div className="settings-actions">
        <button
          type="button"
          className="settings-btn settings-btn--primary"
          onClick={handleChangePassword}
          disabled={saving}
        >
          {saving ? "Updating" : "Update password"}
        </button>
      </div>
    </div>
  );
};

export default PasswordCard;
